'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function parseTime(str: string): number {
  const parts = str.split(':');
  if (parts.length === 2) return parseInt(parts[0]) * 60 + parseFloat(parts[1]);
  return parseFloat(str) || 0;
}

export default function RingtoneMakerTool() {
  const [audioFile, setAudioFile] = useState<{ file: File; url: string; size: number; duration: number } | null>(null);
  const [startTime, setStartTime] = useState('0:00');
  const [endTime, setEndTime] = useState('0:30');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    if (file.size > 500 * 1024 * 1024) { setError('File too large. Max 500 MB.'); return; }
    setError(null); setResultUrl(null);
    const url = URL.createObjectURL(file);
    const audio = new Audio(url);
    audio.onloadedmetadata = () => {
      setAudioFile({ file, url, size: file.size, duration: audio.duration });
      setStartTime('0:00');
      setEndTime(formatTime(Math.min(30, audio.duration)));
    };
    audio.onerror = () => setError('Could not read audio file.');
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files?.[0]; if (f) loadFile(f);
  };

  const duration = audioFile ? parseTime(endTime) - parseTime(startTime) : 0;

  const handleProcess = async () => {
    if (!audioFile) return;
    const start = parseTime(startTime);
    const end = parseTime(endTime);
    if (end <= start) { setError('End time must be after start time.'); return; }
    if (end - start > 40) { setError('Ringtone must be 40 seconds or less.'); return; }

    setProcessing(true); setProgress(0); setError(null); setResultUrl(null);
    try {
      setProgressLabel('Loading FFmpeg engine…'); setProgress(10);
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress: p }) => { setProgress(Math.round(15 + p * 75)); setProgressLabel(`Processing… ${Math.round(p * 100)}%`); });
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      setProgress(15); setProgressLabel('Reading file…');
      const ext = audioFile.file.name.split('.').pop() || 'mp3';
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(audioFile.file));
      await ffmpeg.exec(['-i', `input.${ext}`, '-ss', String(start), '-to', String(end), '-codec:a', 'libmp3lame', '-b:a', '128k', 'ringtone.mp3']);
      setProgress(95); setProgressLabel('Preparing download…');
      const data = await ffmpeg.readFile('ringtone.mp3');
      const blob = new Blob([data], { type: 'audio/mpeg' });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgress(100); setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = 'ringtone.mp3';
    a.click();
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      {!audioFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">📱</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Audio File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports MP3, WAV, OGG, AAC, FLAC, M4A</p>
          <input ref={fileInputRef} type="file" accept=".mp3,.wav,.ogg,.aac,.flac,.m4a" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">📱</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{audioFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(audioFile.size)} · {formatTime(audioFile.duration)}</p>
            </div>
            <button onClick={() => { setAudioFile(null); setResultUrl(null); }} className="text-gray-400 hover:text-red-500 p-1">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Preview</p>
            <audio src={audioFile.url} controls className="w-full h-10" />
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
            <p className="text-amber-700 text-sm font-semibold">Ringtone length: {duration > 0 ? `${duration.toFixed(1)}s` : '—'}</p>
            <p className="text-amber-600 text-xs">Recommended: 20–30 seconds · Max: 40 seconds</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">Start Time (m:ss)</label>
              <input type="text" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-1.5">End Time (m:ss)</label>
              <input type="text" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400" />
            </div>
          </div>
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{progressLabel}</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p className="font-semibold text-green-800 text-sm">Ringtone ready! · {formatBytes(resultSize)}</p>
              </div>
              <audio src={resultUrl} controls className="w-full h-10" />
              <button onClick={handleDownload} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm">⬇️ Download Ringtone (MP3)</button>
            </div>
          )}
          {!resultUrl && (
            <button onClick={handleProcess} disabled={processing} className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm">
              {processing ? 'Creating Ringtone…' : 'Create Ringtone'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
